<template lang="html">
  <section>
    <name-input
      :all-des="allDes"
      :is-ready="true"
      :name="storedDataElement.name"
      :should-focus="!storedDataElement.name"
      :de-id="storedDataElement.id"
      @on-name-input-changed="onDeNameChanged"
    ></name-input>

    <element-description
      :elem-description="storedDataElement.description"
      @on-element-description-changed="onDeDescriptionChanged"
    ></element-description>

    <div class="oo-fields-container">
      <label class="query-label">
        {{ getMessageText(messages.DATA_SOURCE) }}
      </label>
      <div class="ds-select">
        <search-box
          id="oo-ds-search"
          :box-type="'deEditModal'"
          :has-icon="false"
          :label-only="true"
          :list="dataElementList"
          :value="selectedDataSource"
          @on-search-box-item-selected="onSourceSelected"
        />
      </div>
    </div>

    <div class="oo-fields-container" v-if="isSelectedSource">
      <label class="query-label">
        {{ $t("fields") }}
      </label>
      <div class="fields-list">
        <div
          class="field-select"
          v-for="field in expresionFields"
          :key="field.id"
        >
          <text-input
            :id="'s-field-input-' + field.id"
            :value="field.userLabel"
            :focus="field.isEmpty"
            type="text"
            @on-text-input-change="onFieldInputChanged"
          />

          <expressions
            class="f-name"
            :id="'ds-field-' + field.id"
            :data-elements="allDes"
            :selected-source="storedDataElement.selectedSource"
            :initial-expression="field.confExpression"
            :content-type="appType"
            @on-expression-field-valid="onConfigExpressionValid"
          />

          <div class="delete-wrapper">
            <svg
              class="delete-row-icon"
              tabindex="0"
              @keydown.enter.stop="deleteField(field.id)"
              @click="deleteField(field.id)"
            >
              <use
                xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#delete"
              ></use>
            </svg>
          </div>
        </div>
      </div>
    </div>

    <div class="add-btn-container" v-if="isSelectedSource">
      <button ref="addFieldBtn" class="modal-btn" @click="addField">
        {{ $t("add") }}
      </button>
    </div>
  </section>
</template>

<script lang="js">
  import { mapState, mapActions } from "vuex";
  import { v4 as uuidv4 } from "uuid";
  import { MUTATION_NAME } from "@/static/global-variables";
  import SearchBox from "@/components/SearchBox";
  import TextInput from "@/components/TextInput";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import Expressions from "@/components/Expressions"
  import NameInput from "@/components/NameInput";
  import ElementDescription from "@/components/ElementDescription";

  export default {
    name: "one-one-edit",
    components: {
      SearchBox,
      TextInput,
      Expressions,
      NameInput,
      ElementDescription
    },
    props: {
      appId: String,
      de: Object,
      id: String,
      name: String,
      allDes: Object,
      appType: String
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      dataElementList() {
        return this.dataSources.map(e => {
          e.label = e.name;
          return e;
        });
      },
      dataSources() {
        delete this.allDes[this.de.id];
        return expressionBuilder.filterGlobalSources(this.allDes);
      },
      expresionFields() {
        return this.storedDataElement.expresionFields;
      },
      isSelectedSource() {
        return Boolean(this.storedDataElement.selectedSource);
      },
      selectedDataSource() {
        return this.storedDataElement.selectedSource? {
          id: this.storedDataElement.selectedSource.id,
          label: this.storedDataElement.selectedSource.label
        } : null;
      }
    },
    data() {
      return {
        storedDataElement: {
          appId: this.appId,
          id: this.de.id,
          description: this.de.description,
          name: this.de.name,
          type: this.de.type,
          original: this.de.original,
          originLabel: this.de.originLabel,
          selectedSource: null,
          expresionFields: []
        },
        level: 1
      };
    },
    mounted() {
      this.init();
    },
    methods: {
      ...mapActions({
        setDataElement: MUTATION_NAME.SET_EDIT_DATA_ELEMENT
      }),
      init() {
        if (this.de.original) {
          this.storedDataElement.selectedSource = this.dataSources.find(e => e.id === this.de.original);
          // get existing expressions from data element
          const existingExp = expressionBuilder.parseArrayAllLocalExpressions(
            this.de,
            this.allDes
          );
          for (const exp of existingExp) {
            this.addNewExpression(exp);
          }
          this.setDataElement(this.storedDataElement);
        }
      },
      onSourceSelected(val) {
        if (val.isEmpty) {
          this.storedDataElement.original = null;
          this.storedDataElement.originLabel = null;
          this.storedDataElement.selectedSource = null;
          this.storedDataElement.expresionFields = [];
        } else {
          this.storedDataElement.selectedSource = JSON.parse(JSON.stringify(val));
          // add default selected data source expressions from its fields
          const subExpressions = expressionBuilder.parseArrayAllLocalExpressions(
            this.storedDataElement.selectedSource, this.allDes
          );
          for (const exp of subExpressions) {
            this.addNewExpression(exp);
          }
          this.setDataElement(this.storedDataElement);
        }
      },
      onFieldInputChanged(val, inputId) {
        const fieldId = inputId.replace("s-field-input-", "");
        this.storedDataElement.expresionFields.forEach(field => {
          if (field.id === fieldId) {
            field.userLabel = val;
          }
        });
      },
      addField() {
        this.addNewExpression();
      },
      addNewExpression(expression) {
        this.storedDataElement.expresionFields.push({
          id: uuidv4(),
          userLabel: expression? expression.label : "",
          isEmpty: !expression,
          confExpression: expression
        });
      },
      deleteField(id) {
        this.$bvModal.msgBoxConfirm(this.getMessageText(this.messages.ARE_YOU_SURE), {
          size: "sm",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: this.getMessageText(this.messages.OK),
          cancelTitle: this.getMessageText(this.messages.CANCEL),
          centered: true
        }).then(value => {
          if (value) {
            this.storedDataElement.expresionFields = this.storedDataElement.expresionFields.filter(e => e.id !== id);
          }
        })
        .catch(err => {
          console.error(err);
        });
      },
      onConfigExpressionValid(ctx) {
        const fieldId = ctx.id.replace("ds-field-", "");
        const field = this.storedDataElement.expresionFields.find(e => e.id === fieldId);
        field.isEmpty = !ctx.status;
        delete field.confExpression;
        if (ctx.status) {
          field.confExpression = ctx.confExpression;
          field.userLabel = field.userLabel || field.confExpression.label;
        }
        this.setDataElement(this.storedDataElement);
      },
      onDeNameChanged(isValidName, nameInput) {
        this.storedDataElement.name = nameInput;
        this.$emit("on-de-name-changed", isValidName);
        this.setDataElement(this.storedDataElement);
      },
      onDeDescriptionChanged(input) {
        this.storedDataElement.description = input;
        this.setDataElement(this.storedDataElement);
      }
    }
  }
</script>

<style scoped lang="scss">
  .oo-fields-container {
    display: flex;
    flex-direction:column;
    padding: 0 1rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
    min-width: 45rem;
  }

  .add-btn-container {
    display: flex;
    align-items: center;
    padding: 0 1rem;
  }

  .query-label {
    max-width: 100px;
    padding-right: 0.5rem;
    margin-bottom: 0;
  }

  .ds-select,
  .fields-list {
    flex: 3;
  }

  .field-select {
    display: flex;
    justify-content: space-between;
    margin-bottom: 1rem;
    .f-name {
      margin-left: 0.5rem;
      width: 100%;
    }
  }

  .delete-wrapper {
    margin: 0.35rem 0 0 0.5rem;
    .delete-row-icon {
      height: 1.25rem;
      width: 1.25rem;
      fill: var(--SF_ERROR_RED);
      cursor: pointer;
    }
  }
</style>
